import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Form, FormField } from '@/shared/components';
import type { UseFormReturn } from 'react-hook-form';
import {
  type AccountOption,
  type AssetOption,
  type AvailableParams,
  type InternalTransferFormValues,
  type InternalTransferInitialContext,
  type InternalTransferSelectorOption,
  useInternalTransferCardOrders,
  useInternalTransferInitForm,
} from '../model';
import { useInternalTransferExchangeRate } from '../model';
import { InternalAccountSelectorSection } from './InternalAccountSelectorSection';
import { type AccountType, useAccountsStore } from '@/entities/account';
import type { TransferInternalSettingsDictionary } from '@/shared/types';
import { InternalTransferAmountField } from './InternalTransferAmountField';
import { formatAmount, formatReceivedAmount } from '@/shared/lib';
import { NUMERIC_CONFIG } from '@/shared/config';

interface InternalTransferFormProps {
  form: UseFormReturn<InternalTransferFormValues>;
  watchedValues: InternalTransferFormValues;
  accountOptions: AccountOption[];
  selectedAssetFrom?: AssetOption;
  selectedAccountFrom?: AccountOption;
  transferInternalSettings?: TransferInternalSettingsDictionary;
  onMaxClick: (amount: string) => void;
  onFormDirtyChange: (isDirty: boolean) => void;
  initialFromAccountType?: AccountType;
  initialToAccountType?: AccountType;
  setSelectedAssetFrom?: Dispatch<SetStateAction<AssetOption | undefined>>;
  setSelectedAssetTo?: Dispatch<SetStateAction<AssetOption | undefined>>;
  setSelectedAccountFrom?: Dispatch<SetStateAction<AccountOption | undefined>>;
  setSelectedAccountTo?: Dispatch<SetStateAction<AccountOption | undefined>>;
  initialContext?: InternalTransferInitialContext;
  isInitFormData: boolean;
}

const AUTO_SELECT_ACCOUNT_TYPES: AccountType[] = ['ewallet', 'spot'];

export function InternalTransferForm({
  form,
  watchedValues,
  accountOptions,
  selectedAssetFrom,
  selectedAccountFrom,
  transferInternalSettings,
  onMaxClick,
  onFormDirtyChange,
  initialFromAccountType,
  initialToAccountType,
  setSelectedAssetFrom,
  setSelectedAssetTo,
  setSelectedAccountFrom,
  setSelectedAccountTo,
  initialContext,
  isInitFormData,
}: InternalTransferFormProps) {
  const { t } = useTranslation();
  const { ewallet, invest, spot, leverage, earn } = useAccountsStore();

  const { exchangeRateDescription } = useInternalTransferExchangeRate(
    watchedValues.assetFrom,
    watchedValues.assetTo,
    Number(watchedValues.amount)
  );

  const {
    availableAccountTypesTo,
    availableAccountTypesFrom,
    accountsTo,
    accountsFrom,
  } = useInternalTransferCardOrders({
    initialToAccountType,
    initialFromAccountType,
    invest,
    ewallet,
    leverage,
    spot,
    earn,
  });

  useInternalTransferInitForm({
    form,
    isInitFormData,
    initialContext,
    accountOptions,
    accountsFrom,
    accountsTo,
    setSelectedAssetTo,
    setSelectedAccountFrom,
    setSelectedAccountTo,
    setSelectedAssetFrom,
    ewallet,
  });

  const [currentAccountIndexFrom, setCurrentAccountIndexFrom] = useState(0);
  const [currentAccountIndexTo, setCurrentAccountIndexTo] = useState(0);

  const prevCurrentAccountIndexFromRef = useRef<number>(0);
  const prevCurrentAccountIndexToRef = useRef<number>(0);

  const handleFromSlideChange = useCallback((index: number) => {
    setCurrentAccountIndexFrom(index);
  }, []);

  const handleToSlideChange = useCallback((index: number) => {
    setCurrentAccountIndexTo(index);
  }, []);

  const getAccountByType = useCallback(
    (type: AccountType) => {
      return accountOptions.find(acc => acc.type === type);
    },
    [accountOptions]
  );

  const ACCOUNT_SELECTOR_SECTIONS_CONFIG = useMemo<
    InternalTransferSelectorOption[]
  >(
    () => [
      {
        title: t('internalTransferModal.from'),
        totalSlides: availableAccountTypesFrom?.length,
        currentSlide: currentAccountIndexFrom,
        accounts: accountsFrom,
        selectedAccountTypeFrom:
          availableAccountTypesFrom[currentAccountIndexFrom],
        selectedAccountTypeTo: availableAccountTypesTo[currentAccountIndexTo],
        selectedAssetFrom: selectedAssetFrom,
        sectionType: 'from',
        onSlideChange: handleFromSlideChange,
        transferInternalSettings: transferInternalSettings,
        form: form,
        setSelectedAssetFrom: setSelectedAssetFrom,
        setSelectedAssetTo: setSelectedAssetTo,
        setSelectedAccountFrom: setSelectedAccountFrom,
        setSelectedAccountTo: setSelectedAccountTo,
        accountOptions: accountOptions,
      },
      {
        title: t('internalTransferModal.to'),
        totalSlides: availableAccountTypesTo?.length,
        currentSlide: currentAccountIndexTo,
        accounts: accountsTo,
        selectedAccountTypeFrom:
          availableAccountTypesFrom[currentAccountIndexFrom],
        selectedAccountTypeTo: availableAccountTypesTo[currentAccountIndexTo],
        selectedAssetFrom: selectedAssetFrom,
        sectionType: 'to',
        onSlideChange: handleToSlideChange,
        transferInternalSettings: transferInternalSettings,
        form: form,
        setSelectedAssetFrom: setSelectedAssetFrom,
        setSelectedAssetTo: setSelectedAssetTo,
        setSelectedAccountFrom: setSelectedAccountFrom,
        setSelectedAccountTo: setSelectedAccountTo,
        accountOptions: accountOptions,
      },
    ],
    [
      availableAccountTypesFrom,
      availableAccountTypesTo,
      selectedAssetFrom,
      currentAccountIndexFrom,
      currentAccountIndexTo,
      accountsFrom,
      accountsTo,
      transferInternalSettings,
      form,
      handleFromSlideChange,
      handleToSlideChange,
      setSelectedAccountFrom,
      setSelectedAccountTo,
      setSelectedAssetFrom,
      setSelectedAssetTo,
      accountOptions,
    ]
  );

  const { isAmountDisabled, availablePlaceholder, availableParams } =
    useMemo(() => {
      const isLeverageOrInvest =
        selectedAccountFrom?.type === 'leverage' ||
        selectedAccountFrom?.type === 'invest' ||
        selectedAccountFrom?.type === 'earn';
      return {
        isAmountDisabled: !selectedAssetFrom,
        availablePlaceholder: isLeverageOrInvest
          ? 'internalTransferModal.availableMarginBalance'
          : 'internalTransferModal.availableBalance',
        availableParams: selectedAssetFrom
          ? ({
              available: isLeverageOrInvest
                ? formatAmount(
                    (selectedAssetFrom?.equity ?? 0) -
                      (selectedAssetFrom?.margin ?? 0),
                    selectedAssetFrom?.precision
                  )
                : formatAmount(
                    selectedAssetFrom?.available ?? 0,
                    selectedAssetFrom?.precision
                  ),
              margin: isLeverageOrInvest
                ? formatReceivedAmount(selectedAssetFrom?.margin ?? 0)
                : undefined,
              code: selectedAssetFrom.code,
            } as AvailableParams)
          : undefined,
      };
    }, [selectedAccountFrom, selectedAssetFrom]);

  const handleMaxClick = useCallback(() => {
    if (!availableParams?.available) {
      return;
    }
    onMaxClick(
      availableParams?.available
        .replace(
          NUMERIC_CONFIG.decimal.display,
          NUMERIC_CONFIG.decimal.internal
        )
        .replaceAll(' ', '')
    );
  }, [availableParams]);

  const resetFromFields = useCallback(
    (
      setSelectedAccount?: Dispatch<SetStateAction<AccountOption | undefined>>,
      setSelectedAsset?: Dispatch<SetStateAction<AssetOption | undefined>>
    ) => {
      if (setSelectedAccount) {
        setSelectedAccount(undefined);
      }
      if (setSelectedAsset) {
        setSelectedAsset(undefined);
      }
      form.setValue('assetFrom', '');
      form.setValue('amount', '');
      form.clearErrors(['assetFrom', 'amount']);
    },
    [form]
  );

  const resetToFields = useCallback(
    (
      setSelectedAccount?: Dispatch<SetStateAction<AccountOption | undefined>>,
      setSelectedAsset?: Dispatch<SetStateAction<AssetOption | undefined>>
    ) => {
      if (setSelectedAccount) {
        setSelectedAccount(undefined);
      }
      if (setSelectedAsset) {
        setSelectedAsset(undefined);
      }
      form.setValue('assetTo', '');
      form.clearErrors(['assetTo']);
    },
    [form]
  );

  const updateAccountValue = useCallback(
    (
      field: 'accNumFrom' | 'accNumTo',
      currentIndex: number,
      availableTypes: string[]
    ) => {
      const accountType = availableTypes?.[currentIndex] as AccountType;
      if (!accountType) return;

      const isFrom = field === 'accNumFrom';

      const setSelectedAccount = isFrom
        ? setSelectedAccountFrom
        : setSelectedAccountTo;

      const setSelectedAsset = isFrom
        ? setSelectedAssetFrom
        : setSelectedAssetTo;

      if (isFrom) {
        resetFromFields(setSelectedAccount, setSelectedAsset);
      } else {
        resetToFields(setSelectedAccount, setSelectedAsset);
      }

      if (AUTO_SELECT_ACCOUNT_TYPES.includes(accountType)) {
        const account = getAccountByType(accountType);
        if (account) {
          if (setSelectedAccount) {
            setSelectedAccount(account);
          }
          form.setValue(field, account.accNum, {
            shouldValidate: true,
            shouldDirty: true,
          });

          return;
        }
      }

      form.setValue(field, '', {
        shouldValidate: false,
        shouldDirty: true,
      });
      if (setSelectedAccount) {
        setSelectedAccount(undefined);
      }
      if (setSelectedAsset) {
        setSelectedAsset(undefined);
      }
    },
    [
      form,
      getAccountByType,
      setSelectedAccountFrom,
      setSelectedAccountTo,
      setSelectedAssetFrom,
      setSelectedAssetTo,
    ]
  );

  useEffect(() => {
    if (currentAccountIndexFrom === prevCurrentAccountIndexFromRef.current) {
      return;
    }

    prevCurrentAccountIndexFromRef.current = currentAccountIndexFrom;

    updateAccountValue(
      'accNumFrom',
      currentAccountIndexFrom,
      availableAccountTypesFrom
    );
  }, [currentAccountIndexFrom, availableAccountTypesFrom, updateAccountValue]);

  useEffect(() => {
    if (currentAccountIndexTo === prevCurrentAccountIndexToRef.current) {
      return;
    }

    prevCurrentAccountIndexToRef.current = currentAccountIndexTo;

    updateAccountValue(
      'accNumTo',
      currentAccountIndexTo,
      availableAccountTypesTo
    );
  }, [currentAccountIndexTo, availableAccountTypesTo, updateAccountValue]);

  useEffect(() => {
    onFormDirtyChange(form.formState.isDirty);
  }, [form.formState.isDirty, onFormDirtyChange]);

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <Form {...form}>
        <form className="flex flex-col gap-[1.6rem]">
          {ACCOUNT_SELECTOR_SECTIONS_CONFIG.map(section => (
            <InternalAccountSelectorSection
              key={section.sectionType}
              {...section}
            />
          ))}

          <FormField
            control={form.control}
            name="amount"
            render={({ field, fieldState }) => (
              <InternalTransferAmountField
                {...field}
                fieldState={fieldState}
                isAmountDisabled={isAmountDisabled}
                selectedAssetFrom={selectedAssetFrom}
                onMaxClick={handleMaxClick}
                availablePlaceholder={availablePlaceholder}
                availableParams={availableParams}
                exchangeRateDescription={exchangeRateDescription}
              />
            )}
          />
        </form>
      </Form>
    </div>
  );
}
